import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import type { IncidentRow, IncidentsView } from '../../types/safetySystem';
import { shortDate } from './SafetyUnits.columns';
import { YnChip } from './SafetyTickets.columns';

/**
 * Incidents grid column sets — one per V1 view (defs_safety.pl 1623-1736 layouts, mirrored
 * by incidents_query.py). Every view starts with the common incident columns; the child
 * views append their rows' own fields. Identity per view: incidentrecno / invrecno /
 * ierecno / claimrecno / costrecno (COALESCE(claimcost.recno, -associatedcost.recno)).
 */

type Col = ColumnDef<IncidentRow>;

const shortDateTime = (value: unknown): string => {
  const date = shortDate(value);
  if (!date) return '';
  const m = String(value).match(/[T ](\d{2}):(\d{2})/);
  return m ? `${date} ${m[1]}:${m[2]}` : date;
};

const dateCol = (accessorKey: string, header: string, size = 110): Col => ({ accessorKey, header, size, cell: ({ getValue }) => shortDate(getValue()) });
const dateTimeCol = (accessorKey: string, header: string, size = 140): Col => ({ accessorKey, header, size, cell: ({ getValue }) => shortDateTime(getValue()) });
const ynCol = (accessorKey: string, header: string, yesTone: 'neutral' | 'danger' | 'accent' = 'neutral', size = 70): Col => ({
  accessorKey, header, size, cell: ({ getValue }) => <YnChip value={getValue()} yesTone={yesTone} />,
});
const numCol = (accessorKey: string, header: string, size = 90): Col => ({ accessorKey, header, size, meta: { align: 'right' } });
const textCol = (accessorKey: string, header: string, size = 120): Col => ({ accessorKey, header, size });

export function IncidentStatusChip({ status }: { status: unknown }) {
  const s = String(status ?? '').trim().toUpperCase();
  if (!s) return null;
  const closed = s === 'CLOSED';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold leading-4 ${closed ? 'bg-gray-200 text-heading dark:bg-gray-700' : 'bg-accent text-accent-fg'}`}>{s}</span>;
}

/** Common incident columns (every view). */
const COMMON_HEAD: Col[] = [
  numCol('incidentrecno', 'Incident #', 100),
  textCol('gwiclaimnum', 'GWI Claim #', 120),
  { accessorKey: 'incidentstatus', header: 'Status', size: 90, cell: ({ getValue }) => <IncidentStatusChip status={getValue()} /> },
  textCol('incidenttype', 'Type', 150),
  dateTimeCol('incidentdatetime', 'Incident Date'),
  textCol('drivername', 'Driver', 200),
];
const COMMON_TAIL: Col[] = [
  textCol('incidentreportedby', 'Reported By', 130),
  textCol('incidentdept', 'Department', 130),
  textCol('incidentrootcause', 'Root Cause', 140),
  textCol('accountability_group', 'Accountability', 140),
  textCol('incidentlocation', 'Location', 260),
  ynCol('policenotified', 'Police'),
  ynCol('statementrcvd', 'Statement'),
  ynCol('incidentinj', 'Injury', 'danger'),
  ynCol('incidentfat', 'Fatality', 'danger'),
  ynCol('incidenttow', 'Towed'),
  textCol('preventable', 'Preventable', 100),
  numCol('totalclaimreserves', 'Reserves', 100),
  numCol('pronumber', 'Pro #', 100),
  dateCol('incidentsubmitdate', 'Submitted'),
  dateCol('incidentclosedate', 'Closed'),
  { accessorKey: 'incidentdescription', header: 'Description', size: 300 },
  ynCol('incidentdeleted', 'Deleted'),
];

const COMMON_HIDDEN: VisibilityState = {
  incidentreportedby: false, incidentrootcause: false, accountability_group: false, statementrcvd: false, preventable: false,
  totalclaimreserves: false, pronumber: false, incidentsubmitdate: false, incidentdescription: false, incidentdeleted: false,
};

export const INCIDENT_COLUMNS: Col[] = [...COMMON_HEAD, ...COMMON_TAIL];
export const INCIDENT_DEFAULT_HIDDEN: VisibilityState = { ...COMMON_HIDDEN, incidentreportedby: true, incidentrootcause: true, preventable: true };

export const PARTY_COLUMNS: Col[] = [
  ...COMMON_HEAD,
  numCol('invrecno', 'Party #', 90),
  textCol('partytype', 'Party Type', 140),
  textCol('invpartyname', 'Name', 180),
  textCol('unitnumber', 'Unit', 80),
  textCol('invlicensenum', 'License #', 120),
  textCol('invlicensestate', 'Lic State', 80),
  textCol('invphonenum', 'Phone', 120),
  textCol('invadd', 'Address', 180),
  textCol('invcity', 'City', 120),
  textCol('invstate', 'State', 70),
  textCol('invzip', 'Zip', 80),
  ynCol('invcitation', 'Citation'),
  ynCol('invinjured', 'Injured', 'danger'),
  ynCol('invambulance', 'Ambulance'),
  ynCol('invhospital', 'Hospital'),
  ynCol('invfatality', 'Fatality', 'danger'),
  { accessorKey: 'invinjurydesc', header: 'Injury Desc', size: 220 },
  ...COMMON_TAIL,
];
export const PARTY_DEFAULT_HIDDEN: VisibilityState = {
  ...COMMON_HIDDEN, invrecno: false, invadd: false, invcity: false, invstate: false, invzip: false, incidentdept: false,
  incidentlocation: false, policenotified: false, incidentinj: false, incidentfat: false, incidenttow: false, incidentclosedate: false,
};

export const EQUIPMENT_COLUMNS: Col[] = [
  ...COMMON_HEAD,
  numCol('ierecno', 'Equip #', 90),
  textCol('equiptype', 'Equip Type', 140),
  textCol('unitnumber', 'Unit', 80),
  textCol('equipowner', 'Owner', 100),
  textCol('equipownername', 'Owner Name', 160),
  numCol('equippronumber', 'Equip Pro #', 100),
  { accessorKey: 'equipdescription', header: 'Description', size: 220 },
  ynCol('hazmat', 'Hazmat', 'danger'),
  textCol('equipvin', 'VIN', 170),
  ynCol('equipdamaged', 'Damaged'),
  ynCol('equiptowed', 'Towed'),
  textCol('towingloc', 'Towing Loc', 160),
  ynCol('equiptotaled', 'Totaled', 'danger'),
  dateCol('ownernotifieddate', 'Owner Notified'),
  dateCol('cominvrcvddate', 'Com Inv Rcvd'),
  textCol('insurance', 'Insurance', 140),
  textCol('insurancepolicynum', 'Policy #', 120),
  textCol('equipphone', 'Phone', 120),
  { accessorKey: 'damagesdesc', header: 'Damages', size: 220 },
  ...COMMON_TAIL,
];
export const EQUIPMENT_DEFAULT_HIDDEN: VisibilityState = {
  ...COMMON_HIDDEN, ierecno: false, insurance: false, insurancepolicynum: false, equipphone: false, cominvrcvddate: false, incidentdept: false,
  incidentlocation: false, policenotified: false, incidentinj: false, incidentfat: false, incidenttow: false, incidentclosedate: false,
};

export const CLAIM_COLUMNS: Col[] = [
  ...COMMON_HEAD,
  numCol('claimrecno', 'Claim Rec #', 90),
  textCol('claimnumber', 'Claim #', 130),
  textCol('claimtype', 'Claim Type', 150),
  ynCol('inhouse', 'In House'),
  textCol('claimadjuster', 'Adjuster', 140),
  textCol('policynum', 'Policy #', 120),
  textCol('policyholdername', 'Policy Holder', 150),
  textCol('claimpolicytypename', 'Policy Type', 130),
  textCol('chain_policyholder', 'Type Holder', 150),
  textCol('chain_policytype', 'Type Policy', 130),
  numCol('claimamount', 'Claim Amt', 100),
  numCol('claimtotalcosts', 'Total Costs', 100),
  numCol('claimcostspaid', 'Costs Paid', 100),
  numCol('claimgwideductible', 'GWI Ded', 90),
  numCol('claimdriverdeductible', 'Driver Ded', 90),
  textCol('chargebacknum', 'Chargeback', 100),
  textCol('claimchecknum', 'Check #', 100),
  dateCol('claimsubmitdate', 'Submitted'),
  dateCol('claimclosedate', 'Closed'),
  textCol('claimemail', 'Email', 160),
  textCol('claimphone', 'Phone', 120),
  textCol('incidentcustomer', 'Customer', 160),
  ynCol('dotrecordable', 'DOT Rec'),
  { accessorKey: 'claimcomments', header: 'Comments', size: 220 },
  ...COMMON_TAIL,
];
export const CLAIM_DEFAULT_HIDDEN: VisibilityState = {
  ...COMMON_HIDDEN, claimrecno: false, chain_policyholder: false, chain_policytype: false, claimemail: false, claimphone: false,
  claimcomments: false, incidentdept: false, incidentlocation: false, policenotified: false, incidentinj: false, incidentfat: false,
  incidenttow: false,
};

export const COST_COLUMNS: Col[] = [
  ...COMMON_HEAD,
  numCol('costrecno', 'Cost Row #', 90),
  numCol('acrecno', 'Cost #', 90),
  textCol('costtype', 'Cost Type', 140),
  { accessorKey: 'costdesc', header: 'Description', size: 200 },
  textCol('paidtoname', 'Paid To', 160),
  textCol('invoice', 'Invoice', 120),
  numCol('totalaccost', 'Total Cost', 100),
  textCol('paymenttype', 'Payment Type', 120),
  textCol('checknum', 'Check #', 100),
  dateCol('acpaiddate', 'Paid Date'),
  numCol('ccrecno', 'Line #', 80),
  textCol('ccclaimnum', 'Line Claim #', 130),
  textCol('ccliglcode', 'GL Code', 100),
  numCol('cclicost', 'Line Cost', 100),
  ...COMMON_TAIL,
];
export const COST_DEFAULT_HIDDEN: VisibilityState = {
  ...COMMON_HIDDEN, costrecno: false, ccrecno: false, incidentdept: false, incidentlocation: false, policenotified: false,
  incidentinj: false, incidentfat: false, incidenttow: false, incidentclosedate: false,
};

export const VIEW_COLUMNS: Record<IncidentsView, { columns: Col[]; hidden: VisibilityState; title: string }> = {
  incident: { columns: INCIDENT_COLUMNS, hidden: INCIDENT_DEFAULT_HIDDEN, title: 'Incidents — General' },
  invparty: { columns: PARTY_COLUMNS, hidden: PARTY_DEFAULT_HIDDEN, title: 'Incidents — Involved Parties' },
  equipcargo: { columns: EQUIPMENT_COLUMNS, hidden: EQUIPMENT_DEFAULT_HIDDEN, title: 'Incidents — Equipment & Cargo' },
  claim: { columns: CLAIM_COLUMNS, hidden: CLAIM_DEFAULT_HIDDEN, title: 'Incidents — Claims' },
  cost: { columns: COST_COLUMNS, hidden: COST_DEFAULT_HIDDEN, title: 'Incidents — Costs' },
};

export const INCIDENTS_DATE_COLUMNS = [
  'incidentdatetime', 'incidentsubmitdate', 'incidentclosedate', 'ownernotifieddate', 'cominvrcvddate', 'claimsubmitdate', 'claimclosedate', 'acpaiddate',
];
